import { ROOTS, parseRoot, spell, type IntervalName, type RootSlug } from './notes';
import { SCALE_TYPES, TONALITY_FA, getScaleType, isHeptatonic, semis, type ScaleTypeSlug, type Tonality } from './scaleTypes';
import { fold } from '../utils/textNormalize';

export interface Scale {
  /** `<root>-<type>`, for example `a-minor-pentatonic`, `cs-major`. */
  id: string;
  root: RootSlug;
  type: ScaleTypeSlug;
  rootDisplay: string;
  /** Root display plus English type name, for example `A Minor Pentatonic`. */
  nameEn: string;
  /** Persian root name plus Persian type name, for example `لا پنتاتونیک مینور`. */
  nameFa: string;
  tonality: Tonality;
  tonalityFa: string;
  intervals: readonly IntervalName[];
  /** Spelled scale notes, aligned with `intervals`. */
  notes: readonly string[];
  /** 7 for heptatonic scales, 5 for pentatonic and blues. */
  positionCount: number;
}

export const scaleIdOf = (root: RootSlug, type: ScaleTypeSlug): string => `${root}-${type}`;

/** 168 scales: roots C to B, then types in table order. */
export const SCALES: readonly Scale[] = ROOTS.flatMap((r) =>
  SCALE_TYPES.map((t): Scale => ({
    id: scaleIdOf(r.slug, t.slug),
    root: r.slug,
    type: t.slug,
    rootDisplay: r.display,
    nameEn: `${r.display} ${t.nameEn}`,
    nameFa: `${r.fa} ${t.nameFa}`,
    tonality: t.tonality,
    tonalityFa: TONALITY_FA[t.tonality],
    intervals: t.formula,
    // Parent spelling equals spelling each interval from the root, because the blues formulas
    // are supersets of the parent pentatonic with the blue note spelled b5/b3.
    notes: t.formula.map((i) => spell(r.slug, i)),
    positionCount: isHeptatonic(t) ? 7 : 5
  }))
);

const BY_ID = new Map<string, Scale>(SCALES.map((s) => [s.id, s]));
const ORDER = new Map<string, number>(SCALES.map((s, i) => [s.id, i]));

export const getScale = (id: string): Scale | undefined => BY_ID.get(id);
export const scaleNotes = (id: string): readonly string[] => BY_ID.get(id)?.notes ?? [];

/**
 * Step pattern of a heptatonic scale, for example `W-H-W-W-H-W-W`; `H` = 1 semitone, `W` = 2, `A` = 3
 * (augmented second). Returns `null` for pentatonic and blues scales and unknown ids.
 */
export function stepPattern(id: string): string | null {
  const scale = BY_ID.get(id);
  const type = scale && getScaleType(scale.type);
  if (!scale || !type || !isHeptatonic(type)) return null;
  const pcs = type.formula.map(semis);
  return pcs
    .map((s, i) => {
      const step = (i === pcs.length - 1 ? 12 : pcs[i + 1]) - s;
      return step === 1 ? 'H' : step === 2 ? 'W' : step === 3 ? 'A' : '?';
    })
    .join('-');
}

const norm = (s: string): string => fold(s).replace(/[()]/g, ' ');

/** Key tokens accept enharmonic spellings (A# finds Bb). */
const KEY_SLUG = (token: string): RootSlug | null => parseRoot(token);

const FA_ROOTS = ROOTS.map((r) => ({ slug: r.slug, fa: norm(r.fa) })).sort((a, b) => b.fa.length - a.fa.length);

interface SearchKeys {
  words: string;
}

const KEYS = new Map<string, SearchKeys>(
  SCALES.map((s) => {
    const t = getScaleType(s.type)!;
    return [s.id, { words: norm([t.nameEn, t.nameFa, ...t.aliases].join(' ')) }];
  })
);

export interface ScaleSearchFilters {
  /** Root slug, for example `a` or `bb`. */
  key?: RootSlug | null;
  tonality?: Tonality | null;
}

/**
 * Local scale search (AC-29). Filters are ANDed with the query. The query is split on whitespace and every
 * token must match: a single note name (`A`, `Bb`, `A#`) selects the key (enharmonics included); any other
 * token is a substring of the English name, Persian name or an alias. Results keep canonical order
 * (key C to B, then type table order).
 */
export function searchScales(query: string, filters: ScaleSearchFilters = {}): Scale[] {
  const pool = SCALES.filter(
    (s) => (!filters.key || s.root === filters.key) && (!filters.tonality || s.tonality === filters.tonality)
  );
  let q = norm(query).trim();
  // A leading Persian root name («لا», «سی بمل») selects the key; longest names first so «لا بمل» wins over «لا».
  let faKey: RootSlug | null = null;
  for (const r of FA_ROOTS) {
    if (q === r.fa || q.startsWith(r.fa + ' ')) {
      faKey = r.slug;
      q = q.slice(r.fa.length).trim();
      break;
    }
  }
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0 && !faKey) return pool.slice();

  const parsed = tokens.map((tok) => {
    const isKey = /^[a-g][#b♯♭]?$/.test(tok);
    return { tok, key: isKey ? KEY_SLUG(tok) : null };
  });
  return pool
    .filter((s) => {
      if (faKey && s.root !== faKey) return false;
      const hay = KEYS.get(s.id)!.words;
      return parsed.every((p) => (p.key ? p.key === s.root : hay.includes(p.tok)));
    })
    .sort((a, b) => ORDER.get(a.id)! - ORDER.get(b.id)!);
}
